import { listsV2 } from '@ecosystem/newron-design-system';

export type ColumnConfig = Required<Pick<listsV2.ColumnConfig, 'columnKey' | 'columnName'>> & {
  /**
   * Optional prop to enable/disable sorting for the column.
   * @default false
   */
  isSortingEnabled?: boolean;
  /**
   * Get the sorting key for a specific column.
   * @param object The data object to extract the sorting key from.
   * @param key The column key to use for sorting.
   * @returns The sorting key/nested key value.
   */
  getSortingValue?: <T>(object: T, key: keyof T) => string | number | Date;
  /**
   * Optional prop to render value from a nested object or array.
   * Use it for columns that require special rendering logic.
   */
  subComponent?: (cellData?: unknown, cellId?: string) => JSX.Element;
};

/**
 * Configuration for the root (grouping) pane of the tree table.
 */
export type RootConfig<TData extends object> = {
  /**
   * The key in table data to group rows by.
   */
  groupBy: keyof TData;
  /**
   * Header/Title text for the group pane.
   */
  header: string;
  /**
   * The currently selected group value. Optionally used for initial highlighting.
   */
  selectedGroup?: string;
  /**
   * Whether sorting is enabled for the group table.
   */
  isSortingEnabled?: boolean;
  /**
   * Optional prop to get display value from a nested object or array.
   * Use it for columns that require special rendering logic.
   */
  getDisplayValue?: (cellData?: TData[keyof TData]) => string;
  /**
   * Get the sorting key for a root column.
   * @param rootObject The data object to extract the sorting key from.
   * @param rootKey The column key to use for sorting.
   * @returns The sorting key/nested key value.
   */
  getSortingValue?: (rootObject: TData, rootKey: keyof TData) => string | number | Date;
  /**
   * Optional callback when a group is selected.
   */
  onSelectGroup?: (group: string) => void;
};

/**
 * Configuration for the branch (content) pane of the tree table.
 */
export type BranchConfig = {
  /**
   * Memoized column definitions for the branch table.
   */
  columns: ColumnConfig[];
  /**
   * Row selection mode (single/multiple).
   */
  rowSelectionMode?: listsV2.RowSelectionMode;
  /**
   * Optional prop to control initial row selection.
   */
  selectedRows?: Record<string, boolean>;
  /**
   * Optional callback to react on row selection changes.
   */
  onRowSelectionChange?: (selectedRows: Record<string, boolean> | (() => Record<string, boolean>)) => void;
  /**
   * Callback when a row is clicked.
   */
  onRowClick?: (row: listsV2.RowAnalyticsInfo) => void;
  /**
   * Whether to disable the "Select All" checkbox button in the branch table.
   * @default false
   */
  disableSelectAllButton?: boolean;
};

export type Group = {
  label: string;
  count: number;
  sortingValue?: string | number | Date;
};

export interface GroupInfo {
  id: string;
  group: Group;
}

export enum TableType {
  Root = 'root',
  Branch = 'branch',
}
